import { useContext } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import { CallV3Context } from "../state/CallV3Context";
import { addBreadcrumb } from "../config/sentry";

// GMA-1331: render any active CallV3Context.placeCallError as a non-auto-
// hiding MUI snackbar so the caller always sees what went wrong. The retry
// action for popup-blocked must run window.open synchronously inside the
// click handler (preserving the user-gesture) so the browser allows it.
const CallErrorSnackbar = () => {
  const { placeCallError, clearPlaceCallError, placeCall } =
    useContext(CallV3Context);

  if (!placeCallError) return null;

  const handleRetryPopup = () => {
    const url = placeCallError.retryUrl;
    // NB: no "noopener"/"noreferrer" — that returns null even on success, so
    // this retry would open the window but never focus it or clear the error
    // (and each click would spawn another). opener is nulled manually below.
    const popup = window.open(url, "_blank");
    const popupBlocked = !popup;
    addBreadcrumb("call.openCall", {
      category: "call.lifecycle",
      level: popupBlocked ? "warning" : "info",
      stage: "user-retry",
      popupBlocked,
      url,
    });
    if (popup) {
      popup.opener = null;
      popup.focus();
      clearPlaceCallError();
    }
  };

  const handleRetryInit = async () => {
    await placeCall();
  };

  const severity = placeCallError.kind === "popup-blocked" ? "warning" : "error";
  const action =
    placeCallError.kind === "popup-blocked" ? (
      <Button color="inherit" size="small" onClick={handleRetryPopup}>
        Open call window
      </Button>
    ) : (
      <Button color="inherit" size="small" onClick={handleRetryInit}>
        Retry
      </Button>
    );

  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      data-testid="call-error-snackbar"
    >
      <Alert
        severity={severity}
        onClose={clearPlaceCallError}
        action={action}
        sx={{ width: "100%" }}
      >
        {placeCallError.message}
      </Alert>
    </Snackbar>
  );
};

export default CallErrorSnackbar;
